import * as React from 'react'
import { View, Text, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'
import Logger from '@utils/logger'

import { sanitizeScopeKey } from '@screens/admin/roles/utils'

type ScopeCategory =
  | 'sites'
  | 'manifestations'
  | 'locataires-permanents'
  | 'bandeau-pense-bete'
  | 'home'
  | 'prestations'
  | 'demandes'
  | 'statistiques'
  | 'check-list'
  | 'controle-qualite'
  | 'utilisateurs'
  | 'export'
  | 'admin'
  | 'edl'
  | 'notification'

interface Section {
  category: ScopeCategory
  data: string[]
}

interface Props {
  id: string
}

const MAIN_VERTICAL_PADDING = 24

const CATEGORIES = [
  'sites',
  'manifestations',
  'locataires-permanents',
  'bandeau-pense-bete',
  'home',
  'prestations',
  'demandes',
  'statistiques',
  'check-list',
  'controle-qualite',
  'utilisateurs',
  'export',
  'admin',
  'edl',
  'notification',
] as const

const renderItem = (label: string) => (
  <ItemContainer key={label}>
    <IconContainer>
      <Icon name="checkbox_checked" color={Theme.colors.primary} size={24} />
    </IconContainer>
    <ItemValue>{label}</ItemValue>
  </ItemContainer>
)

const AdminUserScreen = ({ id }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const roles = useReducer(AdminStore.store, (s) => s.roles)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')

  const role = React.useMemo(() => roles.find((r) => r.id === id), [roles, id])

  const scopes: Section[] = React.useMemo(
    () =>
      role
        ? CATEGORIES.map((c) => ({ category: c, data: role.scopes.filter((sc) => sc.startsWith(c)) })).filter(
            (s) => s.data.length > 0
          )
        : [],
    [role]
  )

  React.useEffect(() => {
    api.roles
      .all()
      .then(AdminStore.actions.setRoles)
      .catch(Logger.error)
      .finally(() => setStatus('fetched'))
  }, [])

  const onEdit = () => {
    navigation.push(`/admin/roles/form/scope?id=${id}`)
  }

  const onDelete = () => {
    SimpleAlert.open({
      title: i18n.t('common.delete'),
      description: i18n.t('screens.admin.roles.deleting'),
      buttons: [
        {
          label: i18n.t('common.yes'),
          onClick: () => {
            api.roles.delete(id).then(() => navigation.push(`/admin/roles`))
            SimpleAlert.close()
          },
        },
        { label: i18n.t('common.no'), onClick: () => SimpleAlert.close() },
      ],
    })
  }

  const headerLinks: HeaderLink[] = [
    { label: i18n.t('screens.admin.roles.title'), onClick: () => navigation.push('/admin/roles') },
  ]

  const ModifyButton = {
    label: i18n.t('common.modify'),
    onClick: onEdit,
    status: 'active' as ButtonStatus,
  }

  const DeleteButton: HeaderButton = {
    label: i18n.t('common.delete'),
    onClick: onDelete,
    status: 'active' as ButtonStatus,
    color: 'cancel',
  }

  const canEdit = role && !role.readOnly

  const headerButtons = canEdit ? [ModifyButton, DeleteButton] : []

  const renderDemandeSection = (section: Section) => {
    // separated workflow
    const workflow = section.data.filter((d) => d.includes('workflow'))

    const demandesScopes = section.data.filter((d) => !workflow.includes(d))

    return (
      <React.Fragment key={section.category}>
        {demandesScopes.length > 0 && (
          <>
            <SectionTitle title={i18n.t(`screens.admin.roles.categories.demandes`)} />
            <Row>
              {demandesScopes.map((sc) => renderItem(i18n.t(`screens.admin.roles.SCOPE.${sanitizeScopeKey(sc)}`)))}
            </Row>
          </>
        )}

        {workflow.length > 0 && (
          <>
            <SectionTitle title={i18n.t(`screens.admin.roles.categories.demandes_workflow`)} />
            <Row>{workflow.map((sc) => renderItem(i18n.t(`screens.admin.roles.SCOPE.${sanitizeScopeKey(sc)}`)))}</Row>
          </>
        )}
      </React.Fragment>
    )
  }

  const renderSection = (section: Section) => {
    if (section.category === 'demandes') {
      return renderDemandeSection(section)
    }

    return (
      <React.Fragment key={section.category}>
        <SectionTitle title={i18n.t(`screens.admin.roles.categories.${section.category}`)} />
        <Row>{section.data.map((sc) => renderItem(i18n.t(`screens.admin.roles.SCOPE.${sanitizeScopeKey(sc)}`)))}</Row>
      </React.Fragment>
    )
  }

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone && role ? role.name : undefined}
      fixed
      onLeft={navigation.goBack}
      right={canEdit ? 'delete' : undefined}
      onRight={canEdit ? DeleteButton.onClick : undefined}>
      {!!role && status === 'fetched' ? (
        <>
          {!isPhone && (
            <HeaderContainer>
              <Header title={role.name} links={headerLinks} buttons={headerButtons} />
            </HeaderContainer>
          )}

          <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
            <MainContainer>
              {scopes.length > 0 ? (
                scopes.map(renderSection)
              ) : (
                <MissingInfo>{i18n.t('screens.admin.roles.roles.detail.none')}</MissingInfo>
              )}
            </MainContainer>
          </ScrollContainer>
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}

      {isPhone && canEdit && (
        <BottomContainer>
          <ButtonContainer>
            <Button {...ModifyButton} />
          </ButtonContainer>
        </BottomContainer>
      )}
    </Root>
  )
}

export default AdminUserScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const ScrollContainer = styled(ScrollView)`
  flex: 1;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`

const BottomContainer = styled(View)`
  padding: 16px;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)<{ isLeft?: boolean }>`
  flex: 1;
  ${(props) => (props.isLeft ? 'margin-right: 8px' : 'marrgin-left: 8px')};
`

const Row = styled(View)`
  flex-flow: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')} wrap;
  margin: 20px 0;
  margin-bottom: 40px;
`

const ItemContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  width: 350px;
  margin-right: 16px;
  margin-bottom: 16px;
`

const ItemValue = styled(Text)`
  padding: 8px 16px;
  text-align: left;
  ${(props) => props.theme.fonts.Body};
`

const IconContainer = styled(View)`
  margin-right: 12px;
`
